<template>
  <scrollable-container>
    <placeholder
      v-show="isTableAwaiting"
      :text="text"
      @buttonClick="buildReport"
    />
    <loader v-show="isTableLoading" />
    <container
      v-show="isTableReady"
      id="printable"
      :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
    >
      <stat-table
        :class="$style.table"
        :columns="columns"
        :table-data="tableData"
      />
    </container>

    <container
      v-show="isTableReady"
      :class="$style.notScrollableContent"
    >
      <tabset
        :tabs="tabs"
        :opened="opened"
        :tab-style="tabStyle"
        :hidden-text="hiddenText"
        @tabClick="openNewTab"
        @toggle="toggle"
      />
      <barchart
        v-show="tabs[0].active && opened"
        :bars="mostProductivePC.topList"
        :rect-style="mostProductivePC.rectStyle"
        :text-style="mostProductivePC.textStyle"
        :is-convert-needed="true"
      />
      <barchart
        v-show="tabs[1].active && opened"
        :bars="mostUnproductivePC.topList"
        :rect-style="mostUnproductivePC.rectStyle"
        :text-style="mostUnproductivePC.textStyle"
        :is-convert-needed="true"
      />
    </container>
  </scrollable-container>
</template>

<script>
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import Container from '@/components/common/blocks/container.vue';
  import Placeholder from '@/components/common/blocks/report-placeholder.vue';
  import Loader from '@/components/common/blocks/loader.vue';
  import StatTable from '@/views/reports/statistics/stat-table.vue';
  import { getProgramStat } from '@/api/methods/reports/statistics/index';
  import Tabset from '@/components/common/tabs/single-view-tabs.vue';
  import Barchart from '@/components/common/bars/top-bar-chart.vue';
  import { getDataField } from '@/views/reports/users/worktime/table-field';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';

  export default {
    components: {
      ScrollableContainer,
      Container,
      Placeholder,
      StatTable,
      Tabset,
      Barchart,
      Loader,
    },
    data() {
      return {
        name: this.$t('reports.programUsageReport'),
        tableData: [],
        computersArray: [],
        opened: true,
        text: this.$t('reports.pressButtonToGenerateReport'),
        mostProductivePC: {
          topList: [],
          rectStyle: {
            height: '17px',
            fill: '#7CD890',
          },
          textStyle: {
            fill: '#1F8D37',
            fontSize: '12px',
            letterSpacing: '-0.24px',
          },
        },
        mostUnproductivePC: {
          topList: [],
          rectStyle: {
            height: '17px',
            fill: '#FFB0B0',
          },
          textStyle: {
            fill: '#BB5656',
            fontSize: '12px',
            letterSpacing: '-0.24px',
          },
        },
        tabs: [
          {
            title: this.$t('reports.tabs.top5PCsByProductivePrograms'),
            active: true,
          },
          {
            title: this.$t('reports.tabs.top5PCsByUnproductivePrograms'),
            active: false,
          },
        ],
        hiddenText: this.$t('reports.top10Show'),
        tabStyle: {
          padding: '14px 20px 17px 20px',
          fontSize: '16px',
        },
        columns: [
          { name: this.$t('reports.columns.program'), sort: 1, width: '1', field: 'name' },
          { name: this.$t('reports.columns.path'), sort: 0, width: '3', field: 'link' },
          { name: this.$t('reports.columns.activity'), sort: 0, width: '1', field: 'duration' },
          { name: this.$t('reports.columns.productivity'), sort: 0, width: '1', field: 'productivity' },
        ],
      };
    },
    computed: {
      isPdfPage() {
        return this.$route.query.pdf === 'true';
      },
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      checkedComputers() {
        return this.$store.getters['pageSpecificData/checkedComputersList'];
      },
      computersTree() {
        const tree = this.$store.getters['pageSpecificData/computersTree'];
        return tree;
      },
    },
    watch: {
      computersTree: {
        async handler(newVal) {
          if (this.isPdfPage && newVal.length) {
            await this.buildReport();
            await this.$nextTick();

            await new Promise((resolve) => {
              setTimeout(resolve, 1000);
            });
            // eslint-disable-next-line no-underscore-dangle
            window.__PDF_READY__ = true;
          }
        },
        immediate: true,
      },
      dateRange(oldValue, newValue) {
        if (
          oldValue.dateFrom.toString() !== newValue.dateFrom.toString() ||
          oldValue.dateTo.toString() !== newValue.dateTo.toString()
        ) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      checkedComputers(oldArray, newArray) {
        if (oldArray !== newArray) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
    },
    mounted() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
    },
    methods: {
      openNewTab(index) {
        this.tabs.forEach((item) => {
          item.active = false;
        });

        this.tabs[index].active = true;
      },
      toggle() {
        this.opened = !this.opened;
      },
      getCheckedComputersFromTree(node) {
        if (node.type === 'computer' && node.checked) {
          this.computersArray.push(node);
        }
        if (node.type === 'folder') {
          node.children.forEach((child) => this.getCheckedComputersFromTree(child));
        }
      },
      async buildReport() {
        this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
        this.tableData = [];
        this.computersArray = [];
        const compInStorage = JSON.parse(localStorage.getItem('selectedComputers'));
        if (this.isPdfPage && compInStorage?.length) {
          this.computersArray = compInStorage;
        } else if (this.computersTree) {
          this.computersTree.forEach((node) => this.getCheckedComputersFromTree(node));
        }

        if (this.dateRange.dateFrom && this.dateRange.dateTo && this.computersArray) {
          const { data } = await getProgramStat(this.computersArray, this.dateRange.dateFrom, this.dateRange.dateTo);
          if (data) {
            this.tableData = data.result.users;
            this.mostProductivePC.topList = data.result.productivePC;
            this.mostUnproductivePC.topList = data.result.unProductivePC;

            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);

            const exportArray = [];

            for (let i = 0; i < data.result.users.length; i++) {
              const name = data.result.users[i].userName;

              Object.entries(data.result.users[i].history).forEach(([key, value]) => {
                const category = key;

                for (let y = 0; y < value.events.length; y++) {
                  const resObj = { Пользователь: name, Категория: category };
                  for (let j = 0; j < this.columns.length; j++) {
                    resObj[this.columns[j].name] = getDataField(
                      value.events[y][this.columns[j].field],
                      this.columns[j].field,
                    );
                  }
                  exportArray.push(resObj);
                }
              });
            }

            this.$store.commit('exportData/setPrograms', { name: this.name, data: exportArray });
          } else {
            this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
          }
        }
        return this.tableData;
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .notScrollableContent {
    margin: 20px 8px 0 0;
    padding: 0 12px 10px 20px;
    overflow: unset;
  }
</style>
